import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const AdminBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form State
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    content: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setBlogs(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      alert('Error fetching blogs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', author: '', content: '' });
    setSelectedImage(null);
    setCurrentImageUrl('');
    setEditingId(null);
    setIsFormVisible(false);
  };

  const handleEdit = (blog) => {
    setFormData({
      title: blog.title,
      author: blog.author,
      content: blog.content
    });
    setCurrentImageUrl(blog.image_url);
    setSelectedImage(null);
    setEditingId(blog.id);
    setIsFormVisible(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      alert("Title and content are required.");
      return;
    }

    setSaving(true);
    
    try {
      let finalImageUrl = currentImageUrl;

      // Handle image upload if a new image is selected
      if (selectedImage) {
        const fileExt = selectedImage.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random()}.${fileExt}`;
        const filePath = `blogs/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('hbb_files')
          .upload(filePath, selectedImage);
          
        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('hbb_files')
          .getPublicUrl(filePath);
          
        finalImageUrl = publicUrl;
      }

      const blogData = {
        title: formData.title,
        author: formData.author || 'Admin',
        content: formData.content,
        image_url: finalImageUrl
      };

      if (editingId) {
        // Update existing blog
        const { error: updateError } = await supabase
          .from('blogs')
          .update(blogData)
          .eq('id', editingId);
          
        if (updateError) throw updateError;
      } else {
        // Create new blog
        const { error: insertError } = await supabase
          .from('blogs')
          .insert([blogData]);
          
        if (insertError) throw insertError;
      }
      
      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Save failed: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      try {
        const { error } = await supabase.from('blogs').delete().eq('id', id);
        if (error) throw error;
        fetchBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
        alert('Delete failed: ' + error.message);
      }
    }
  };

  return (
    <div className="p-8 min-h-screen bg-[#131313] text-white">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-[#f59e0b]" style={{ fontFamily: 'Bebas Neue' }}>Manage Blogs</h2>
        {!isFormVisible && (
          <button 
            onClick={() => setIsFormVisible(true)}
            className="bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174] font-bold py-2 px-4 rounded transition-colors flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Create Blog Post
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[#a08e7a] p-4 bg-[#1c1b1b] rounded-xl border border-white/10 mb-8">
          <span className="material-symbols-outlined animate-spin">sync</span>
          <span>Loading blogs...</span>
        </div>
      )}

      {isFormVisible && (
        <div className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 mb-8 shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-[#d8c3ad]">{editingId ? 'Edit Blog Post' : 'Create New Blog Post'}</h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-white transition-colors">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-400 mb-2">Title *</label>
              <input 
                type="text" 
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full focus:border-[#f59e0b] focus:outline-none transition-colors" 
                required 
                placeholder="Enter blog title"
              />
            </div>
            
            <div className="md:col-span-1">
              <label className="block text-sm text-gray-400 mb-2">Author Name</label>
              <input 
                type="text" 
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full focus:border-[#f59e0b] focus:outline-none transition-colors" 
                placeholder="e.g. John Doe"
              />
            </div>

            <div className="md:col-span-1">
              <label className="block text-sm text-gray-400 mb-2">Cover Image</label>
              <input 
                type="file" 
                accept="image/*"
                onChange={handleImageChange}
                className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full text-sm" 
              />
              {currentImageUrl && !selectedImage && (
                <div className="mt-2 flex items-center gap-2 text-xs text-[#a08e7a]">
                  <span className="material-symbols-outlined text-sm text-green-400">check_circle</span>
                  Current image attached
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm text-gray-400 mb-2">Content *</label>
              <textarea 
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows="8"
                className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white w-full focus:border-[#f59e0b] focus:outline-none transition-colors" 
                required 
                placeholder="Write your blog content here..."
              ></textarea>
            </div>
            
            <div className="md:col-span-2 flex justify-end gap-3 mt-4">
              <button 
                type="button" 
                onClick={resetForm}
                disabled={saving}
                className="px-6 py-2 rounded text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={saving}
                className={`px-8 py-2 font-bold rounded transition-colors flex items-center gap-2 ${saving ? 'bg-gray-600 text-gray-300 cursor-not-allowed' : 'bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174]'}`}
              >
                {saving ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-sm">sync</span>
                    Processing...
                  </>
                ) : (
                  editingId ? 'Update Blog Post' : 'Publish Blog Post'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {(!loading && !isFormVisible) && (
        <div className="bg-[#1c1b1b] rounded-xl border border-white/10 overflow-hidden shadow-xl">
          <div className="p-4 bg-white/5 border-b border-white/10">
            <h3 className="text-lg font-semibold text-[#d8c3ad]">All Blog Posts ({blogs.length})</h3>
          </div>
          {blogs.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <span className="material-symbols-outlined text-4xl mb-2 opacity-50">article</span>
              <p>No blog posts found. Create your first one!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="border-b border-white/5 bg-[#0e0e0e]">
                    <th className="px-6 py-4 font-normal text-[#a08e7a]">Title</th>
                    <th className="px-6 py-4 font-normal text-[#a08e7a]">Author</th>
                    <th className="px-6 py-4 font-normal text-[#a08e7a]">Date</th>
                    <th className="px-6 py-4 font-normal text-[#a08e7a] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map(blog => {
                    const date = new Date(blog.created_at).toLocaleDateString();
                    return (
                      <tr key={blog.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-6 py-4 font-medium">
                          <div className="flex items-center gap-3">
                            {blog.image_url ? (
                              <img src={blog.image_url} alt="thumbnail" className="w-10 h-10 object-cover rounded shadow border border-white/10" />
                            ) : (
                              <div className="w-10 h-10 rounded bg-[#2a2a2a] flex items-center justify-center border border-white/10">
                                <span className="material-symbols-outlined text-gray-500 text-sm">image</span>
                              </div>
                            )}
                            <div className="truncate max-w-xs">{blog.title}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-400">{blog.author || 'Admin'}</td>
                        <td className="px-6 py-4 text-gray-400">{date}</td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button onClick={() => handleEdit(blog)} className="text-[#f59e0b] hover:text-[#ffc174] p-2 rounded hover:bg-white/5 transition-colors" title="Edit">
                            <span className="material-symbols-outlined text-[20px]">edit</span>
                          </button>
                          <button onClick={() => handleDelete(blog.id)} className="text-red-400 hover:text-red-300 p-2 rounded hover:bg-red-400/10 transition-colors" title="Delete">
                            <span className="material-symbols-outlined text-[20px]">delete</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
